import type { LiveNode, LiveWorkflow } from './diff.js';
import { loginCredentialRef } from './authored-fields.js';
import { str, yamlScalar } from './pull-values.js';

type FrontmatterWorkflow = LiveWorkflow & {
  tags?: Array<{ name: string }> | null;
};

export function renderFrontmatter(
  workflow: FrontmatterWorkflow,
  authLogin: LiveNode | undefined,
  opts: { requirementRef?: string } = {}
): string[] {
  const lines: string[] = [];
  // An auth login → authenticated, whether or not we can name its credential
  // (an empty/UUID-only login still round-trips via emit's bare login).
  const authenticated = !!authLogin;
  const actor = authLogin ? inferActor(authLogin) : null;
  lines.push('---');
  lines.push(`name: ${yamlScalar(workflow.name)}`);
  lines.push(`workflowId: ${workflow.id}`);
  // Only user-settable approval-axis values round-trip; omit system states
  // (generating/building/…) so re-compile leaves status to the server.
  if (workflow.status === 'draft' || workflow.status === 'approved') {
    lines.push(`status: ${workflow.status}`);
  }
  if (workflow.flowType && workflow.flowType !== 'standard') {
    lines.push(`flowType: ${workflow.flowType}`);
  }
  if (actor) lines.push(`actor: ${yamlScalar(actor)}`);
  lines.push(`authenticated: ${authenticated ? 'true' : 'false'}`);
  if (workflow.apiOnly === true) lines.push('apiOnly: true');
  if (workflow.viewportSize !== undefined) {
    lines.push(`viewportSize: ${workflow.viewportSize ?? 'null'}`);
  }
  const tags = (workflow.tags ?? []).map((t) => t.name).filter(Boolean);
  if (tags.length > 0) lines.push(`tags: [${tags.map(yamlScalar).join(', ')}]`);
  const envBlock = renderEnvironments(workflow.environmentConstraints);
  if (envBlock) lines.push(...envBlock);
  if (workflow.sandboxTemplate?.name) {
    lines.push(`sandboxTemplate: ${yamlScalar(workflow.sandboxTemplate.name)}`);
  }
  const teardownLines = renderTeardown(workflow);
  if (teardownLines) lines.push(...teardownLines);
  // `requirementRef` is a spec-source-only field — the live workflow has no
  // column for it, so pull preserves it from the prior file via the CLI's
  // `preserveFrontmatter` merge. `opts.requirementRef` lets the caller force
  // a specific value (e.g. on first pull where no prior file exists).
  if (opts.requirementRef) lines.push(`requirementRef: ${yamlScalar(opts.requirementRef)}`);
  lines.push('---');
  lines.push('');
  return lines;
}

/**
 * Render `teardown:` frontmatter for seed workflows. Uses the bare-string
 * shorthand when defaults hold (snapshotVersion=latest, enabled=true); falls
 * back to the nested form when the author has pinned a snapshot or disabled.
 * The teardown name comes from the GET response's resolved `teardownWorkflowName`
 * — pull skips emission when the link is set but the name didn't resolve
 * (graceful, lets the follow-up endpoint enrichment kick in transparently).
 */
function renderTeardown(workflow: LiveWorkflow): string[] | null {
  if (!workflow.teardownWorkflowId) return null;
  const name = workflow.teardownWorkflowName;
  if (!name) return null;
  const snap = workflow.teardownSnapshotVersion;
  const enabled = workflow.teardownEnabled !== false;
  // Shorthand: defaults hold.
  if ((snap === null || snap === undefined) && enabled) {
    return [`teardown: ${yamlScalar(name)}`];
  }
  // Nested form for non-defaults.
  const out = ['teardown:', `  spec: ${yamlScalar(name)}`];
  if (snap !== null && snap !== undefined) out.push(`  snapshotVersion: ${snap}`);
  if (!enabled) out.push('  enabled: false');
  return out;
}

/**
 * Render `environmentConstraints` from the API GET shape (env *ids*, optionally
 * with names) into the spec frontmatter form (names only). Returns the YAML
 * lines for the `environments:` key, or null when no constraints are set.
 *
 * We deliberately omit the constraint when names aren't available — the GET
 * response is the only place to resolve id → name, and emitting bare ids
 * would not round-trip through the spec author's editing flow.
 */
function renderEnvironments(constraints: LiveWorkflow['environmentConstraints']): string[] | null {
  if (!constraints) return null;
  if (constraints.mode === 'only') {
    const names = constraints.environmentNames;
    if (!names || names.length === 0) return null;
    return ['environments:', `  only: [${names.map(yamlScalar).join(', ')}]`];
  }
  return ['environments:', `  types: [${constraints.types.map(yamlScalar).join(', ')}]`];
}

function inferActor(login: LiveNode): string | null {
  const c = login.configJson ?? {};
  // `from_seed` wins first so the dedicated syntax (`seed-output <ref>`)
  // round-trips faithfully instead of being lumped under `credential`.
  if (c.credentialSource === 'from_seed') {
    const seedRef = c.seedCredentialRef as Record<string, unknown> | undefined;
    const ref =
      (typeof seedRef?.dataKey === 'string' && seedRef.dataKey) ||
      (typeof seedRef?.name === 'string' && seedRef.name);
    if (ref) return `seed-output ${ref}`;
  }
  const role = str(c.credentialTemplateName);
  if (role) return role;
  // Static / template-by-id fall back to `credential <ref>` — uses the same
  // identity string as the hash, so it round-trips without churn regardless
  // of which static-binding mode the row stored.
  const cred = loginCredentialRef(c);
  if (cred) return `credential ${cred}`;
  return null;
}
